import ActionButton from 'components/ActionButton';
import AddUserHomeBoxPreferenceModal from 'components/AddUserHomeBoxPreferenceModal';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import EditActionForTable from 'components/EditActionForTableData';
import { Loading } from 'components/loading/Loading';
import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { deleteUserInventoryItemPreference, fetchUserInventoryItemPreferencesForAdmin } from 'services/api/users';
import { getPageLimit, showError, showSuccess, updatePageLimit } from 'utils';
import styles from './index.module.scss';
import qs from 'query-string';
import CustomLink from 'components/TableLink';

const UserHomeBoxPreferences = props => {
  const [loading, setLoading] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [preferenceToEdit, setPreferenceToEdit] = useState<Record<string, any>>({});
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [preferences, setPreferences] = useState<Record<string, any>>({});
  const { userId } = props;
  let filters: { [p: string]: any; page: number } = {
    page: 0,
  };

  const fetchUserPreferences = async () => {
    try {
      const response = await fetchUserInventoryItemPreferencesForAdmin({
        ...filters,
        userId,
        limit: getPageLimit(),
      });
      setPreferences(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };
  
  const queryStringToFilter = () => {
    return qs.parse(props.location.search, { parseBooleans: true, parseNumbers: true });
  };

  useEffect(() => {
    filters = {
      ...filters,
      ...queryStringToFilter(),
    };
    fetchUserPreferences();
  }, []);

  const handleEdit = (data: Record<string, any>) => {
    setPreferenceToEdit(data);
    setModalOpen(true);
    setIsEdit(true);
  };

  const handleDelete = async (preferenceId: string) => {
    try {
      await deleteUserInventoryItemPreference({ preferenceId });
      await fetchUserPreferences();
      showSuccess('Preference deleted succesfully');
    } catch (e) {
      showError(e);
    }
  };

  const columns = useMemo(() => [
    {
      name: 'Preference ID',
      selector: 'objectId',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/inventory-items/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: 'inventoryItem.name',
    },
    {
      name: 'Probability',
      selector: 'probability',
    },
    {
      name: 'Action',
      selector: '',
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'d-flex ml-1'}>
          <div>
            <EditActionForTable
              onClick={() => handleEdit(row)}
            />
          </div>
          <div className={'ml-4'}>
            <DeleteActionForTable
              onClick={() => handleDelete(row.objectId)}
            />
          </div>
        </div>
      ),
    },
  ], [preferences]);
  
  const filterToQueryString = () => {
    return qs.stringify({ ...filters });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onChangePage = (page: number) => {
    setLoading(true);
    filters.page = page - 1;
    updateQueryString();
    fetchUserPreferences();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  if (loading) return <Loading type={'mini'} />;

  return (
    <div>
      {modalOpen && (
        <AddUserHomeBoxPreferenceModal
          closeModal={() => {
            setIsEdit(false);
            setModalOpen(false);
          }}
          isEdit={isEdit}
          onSuccess={fetchUserPreferences}
          preferenceToEdit={preferenceToEdit}
          userId={userId}
        />
      )}
      <div className={styles['homebox-section-header']}>
        <div className={styles['section-heading']}>User Home Box Preferences</div>
        <ActionButton
          text={<>Preference <span className={'ml-3'}>+</span></>}
          active={true}
          isLoading={false}
          onClick={() => setModalOpen(true)}
        />
      </div>
      <DataTable
        noHeader
        data={preferences.items}
        columns={columns}
        customStyles={tableStyles}
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={preferences.total}
        paginationRowsPerPageOptions={[50, 100, 200]}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    </div>
  );
};

export default UserHomeBoxPreferences;